import React, { useState, useEffect, useCallback } from "react";
import {
  makeStyles,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tooltip,
  IconButton,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  InputAdornment,
} from "@material-ui/core";
import {
  TURNOS_CORRELATION_BY_META,
  TURNOS_CORRELATION_BY_INTERNAL,
  TURNOS_INTERNAL_LABELS,
  TURNOS_DEFAULT_INTERNAL_TEXT,
  TURNOS_INTERNAL_WITHOUT_META,
  TURNOS_WINDOW_HELP,
} from "../../helpers/turnosMessageCorrelation";
import TurnosTemplateOverrides from "../../components/TurnosTemplateOverrides";

// Idiomas de las plantillas de rubro. "" = Neutral (null en backend) — sin
// vocabulario propio, sirve para cualquier empresa. Espejo de SUPPORTED_LOCALES
// del backend (RubroTemplateController).
const RUBRO_LOCALE_OPTIONS = [
  { value: "", label: "Neutral (cualquier idioma)" },
  { value: "es-AR", label: "Español (es-AR)" },
  { value: "it-IT", label: "Italiano (it-IT)" },
  { value: "en-US", label: "Inglés (en-US)" },
  { value: "pt-BR", label: "Portugués (pt-BR)" },
];

const localeChipLabel = (loc) => {
  if (!loc) return "Neutral";
  const o = RUBRO_LOCALE_OPTIONS.find((x) => x.value === loc);
  return o ? o.label.replace(/\s*\(.*\)$/, "") + ` (${loc})` : loc;
};

// Términos configurables por rubro (orden = orden de la respuesta `keys` del backend).
// Singular junto a su plural para la grilla de 2 columnas del editor.
// Vacío = no seteado = hereda el default del locale en runtime.
const TERMINOLOGY_FIELDS = [
  { key: "patient", label: "Paciente" },
  { key: "patientPlural", label: "Pacientes" },
  { key: "patientName", label: "Nombre del paciente" },
  { key: "patientPhone", label: "Teléfono del paciente" },
  { key: "professional", label: "Profesional" },
  { key: "professionalPlural", label: "Profesionales" },
  { key: "appointment", label: "Turno" },
  { key: "appointmentPlural", label: "Turnos" },
  { key: "specialty", label: "Especialidad" },
  { key: "specialtyPlural", label: "Especialidades" },
  { key: "service", label: "Servicio" },
  { key: "servicePlural", label: "Servicios" },
  { key: "waitingRoom", label: "Sala de espera" },
  { key: "waitingRoomPlural", label: "Salas de espera" },
  { key: "cobertura", label: "Cobertura" },
  { key: "coberturaPlural", label: "Coberturas" },
  { key: "scheduler", label: "Quien agenda (mostrador)" },
  { key: "schedulerPlural", label: "Quienes agendan" },
  { key: "room", label: "Consultorio / box" },
  { key: "roomPlural", label: "Consultorios / boxes" },
  // Marcador de género: UNA letra ("m" o "f"). De acá sale el artículo que el
  // panel escribe delante de la palabra ("la Sucursal", no "el Sucursal").
  // Vacío = hereda el género del locale. Ver helpers/terminologyGrammar.
  { key: "patientGen", label: "Género · paciente (m/f)" },
  { key: "professionalGen", label: "Género · profesional (m/f)" },
  { key: "appointmentGen", label: "Género · turno (m/f)" },
  { key: "specialtyGen", label: "Género · especialidad (m/f)" },
  { key: "serviceGen", label: "Género · servicio (m/f)" },
  { key: "waitingRoomGen", label: "Género · sala de espera (m/f)" },
  { key: "coberturaGen", label: "Género · cobertura (m/f)" },
  { key: "schedulerGen", label: "Género · quien agenda (m/f)" },
  { key: "roomGen", label: "Género · consultorio (m/f)" },
];

// Fallback de los defaults del locale español (espejo de LOCALE_TERMINOLOGY_DEFAULTS.es
// del backend TurnosIA). Se usa para el placeholder "hereda: paciente" si el backend
// todavía no devuelve `localeDefaults` (imagen sin rebuild). El backend, cuando responde,
// pisa este mapa.
const LOCALE_DEFAULTS_ES_FALLBACK = {
  patient: "paciente", patientPlural: "pacientes",
  patientName: "Nombre del paciente", patientPhone: "Teléfono",
  professional: "profesional", professionalPlural: "profesionales",
  appointment: "turno", appointmentPlural: "turnos",
  specialty: "especialidad", specialtyPlural: "especialidades",
  service: "servicio", servicePlural: "servicios",
  waitingRoom: "sala de espera", waitingRoomPlural: "salas de espera",
  cobertura: "cobertura", coberturaPlural: "coberturas",
  scheduler: "agendador", schedulerPlural: "agendadores",
  room: "consultorio", roomPlural: "consultorios",
  patientGen: "m", professionalGen: "m", appointmentGen: "m", specialtyGen: "f",
  serviceGen: "m", waitingRoomGen: "f", coberturaGen: "f", schedulerGen: "m", roomGen: "m",
};

// Planes comerciales — gatean los MODOS DE OPERACIÓN que el admin puede elegir.
// El backend (TurnosIA PlanService) es la fuente de verdad; esto es el espejo
// para el selector. `enables` = qué modos habilita cada plan (texto informativo).
const PLAN_OPTIONS = [
  { value: "turnos",        label: "Turnos",           enables: "Solo modo Desatendido (la IA atiende sola)." },
  { value: "turnos_agenda", label: "Turnos + Agenda",  enables: "Desatendido y Atendido (secretaria/agendador)." },
  { value: "turnos_chat",   label: "Turnos + Chat",    enables: "Desatendido, Atendido y Con chat (deriva a humano)." },
  { value: "enterprise",    label: "Enterprise",       enables: "Todos los modos (Desatendido, Atendido, Con chat)." },
];

// Días de la ventana de envío (orden lun→dom, código = backend ReminderWindowService).
const RW_DAYS = [
  { code: "lun", label: "Lun" },
  { code: "mar", label: "Mar" },
  { code: "mie", label: "Mié" },
  { code: "jue", label: "Jue" },
  { code: "vie", label: "Vie" },
  { code: "sab", label: "Sáb" },
  { code: "dom", label: "Dom" },
];

const rwToForm = (w) => ({
  horaDesde: w?.horaDesde || "09:00",
  horaHasta: w?.horaHasta || "21:00",
  diasEvitar: Array.isArray(w?.diasEvitar) ? [...w.diasEvitar] : [],
  sabadoHasta: w?.sabadoHasta ?? null,
});
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ClearIcon from "@material-ui/icons/Clear";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { toast } from "react-toastify";
import api from "../../services/api";

// URL pública de TurnosIA — solo para mostrar en la doc (Base URL / ejemplo curl).
// Las llamadas superadmin NO pegan directo acá: van por el proxy del backend WAIA
// (/superadmin/turnos/*), que adjunta el token server-side (feat_208).
const turnosApiUrl =
  (typeof window !== "undefined" && window.ENV?.VITE_TURNOS_URL) ||
  "https://apiwaia.zzia.info/turnosIA";

const METHOD_COLORS = {
  GET: "#10b981",
  POST: "#3b82f6",
  PATCH: "#f59e0b",
  DELETE: "#ef4444",
  PUT: "#8b5cf6",
};

const CATEGORY_ORDER = [
  "Turnos",
  "Profesionales",
  "Disponibilidad",
  "Lista de Espera",
  "Rebalanceo",
  "Recordatorios",
  "Configuración",
  "Superadmin",
  "Sistema",
];

const getCategory = (endpoint) => {
  const path = endpoint.replace(/^(GET|POST|PATCH|DELETE|PUT)\s+/, "");
  if (path.startsWith("/agenda") || path.startsWith("/appointments")) return "Turnos";
  if (path.startsWith("/professionals")) return "Profesionales";
  if (path.startsWith("/slots") || path.startsWith("/availability")) return "Disponibilidad";
  if (path.startsWith("/waitinglist")) return "Lista de Espera";
  if (path.startsWith("/rebalance")) return "Rebalanceo";
  if (path.startsWith("/reminders")) return "Recordatorios";
  if (path.startsWith("/company")) return "Configuración";
  if (path.startsWith("/superadmin")) return "Superadmin";
  return "Sistema";
};

const getMethod = (endpoint) => endpoint.split(" ")[0];
const getPath = (endpoint) => endpoint.slice(endpoint.indexOf(" ") + 1);

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(4), maxWidth: 1180, margin: "0 auto" },
  headerBar: { display: "flex", alignItems: "center", gap: theme.spacing(1.5), marginBottom: theme.spacing(0.5) },
  pageSubtitle: { marginBottom: theme.spacing(3), color: theme.palette.text.secondary },
  tabs: { marginBottom: theme.spacing(3), borderBottom: `1px solid ${theme.palette.divider}` },

  // Tarjeta de sección — molde del tab Ajustes de TurnosIA (aire + jerarquía).
  section: { marginBottom: theme.spacing(3), padding: theme.spacing(3), borderRadius: 10 },
  sectionTitle: { fontWeight: 700, marginBottom: theme.spacing(0.5) },
  sectionDesc: { marginBottom: theme.spacing(2), color: theme.palette.text.secondary },

  // Tabla con más aire (headers legibles, filas altas, hover prolijo).
  tableCard: { marginBottom: theme.spacing(3), borderRadius: 10, overflow: "hidden" },
  tableTitle: { padding: theme.spacing(2, 2.5), fontWeight: 700 },
  table: {
    "& .MuiTableCell-root": {
      padding: theme.spacing(1.75, 2.5),
      fontSize: 14,
      borderBottomColor: theme.palette.divider,
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      fontWeight: 700,
      fontSize: 12,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.action.hover,
    },
    "& .MuiTableBody-root .MuiTableRow-root:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  statusChip: { height: 24, borderRadius: 6, fontWeight: 600, fontSize: 11 },

  codeBlock: {
    backgroundColor: theme.palette.type === "dark" ? "#1e1e2e" : "#f4f4f8",
    borderRadius: 8,
    padding: theme.spacing(2),
    fontFamily: "monospace",
    fontSize: 12,
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
    marginTop: theme.spacing(0.5),
    overflowX: "auto",
    position: "relative",
  },
  method: {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 4,
    fontWeight: "bold",
    fontSize: 11,
    marginRight: 10,
    color: "#fff",
    flexShrink: 0,
  },
  categoryHeader: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    borderBottom: `2px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(0.75),
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1.5),
    fontSize: 15,
    letterSpacing: 0.3,
  },
  accordion: {
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    "&:before": { display: "none" },
  },
  searchBar: { marginBottom: theme.spacing(2) },
  copyBtn: {
    position: "absolute",
    top: 6,
    right: 6,
    padding: 4,
  },
  curlWrapper: { position: "relative" },
  settingRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  settingKey: { fontWeight: 600, fontSize: 13, color: theme.palette.text.secondary },
  settingVal: { fontSize: 13, wordBreak: "break-all", textAlign: "right" },
  infoBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,193,7,0.12)" : "#fff8e1",
    borderRadius: 8,
    borderLeft: "3px solid #ffc107",
    marginTop: theme.spacing(2),
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
const FEATURE_DEFS = [
  { key: "featureWaitingList",   label: "Lista de espera y rebalanceo",       desc: "Gestión de pacientes en espera y relleno automático de turnos cancelados" },
  { key: "featureSalaDeEspera",  label: "Sala de espera",                     desc: "Pantalla pública para mostrar en TV del consultorio" },
  { key: "featureChat",          label: "Chat interno",                       desc: "Comunicación entre agendadores y profesionales" },
  { key: "featureAgendador",     label: "Rol agendador",                      desc: "Usuario con acceso limitado para cargar turnos manualmente" },
  { key: "featureSobreturnos",   label: "Sobreturnos",                        desc: "Habilitación de turnos extra y sugerencia inteligente" },
  { key: "featureSpecialBlocks", label: "Bloques especiales",                 desc: "Agenda extendida del profesional para procedimientos complejos invisibles al paciente" },
  { key: "featurePrecios",       label: "Lista de precios",                   desc: "Carga la lista de precios desde un archivo (planilla en grilla o CSV) y la administra desde el panel" },
  { key: "featureImportTurnos",  label: "Carga de turnos por Excel",          desc: "El admin sube el listado del sistema anterior: crea los turnos que faltan y cancela los importados que ya no figuran. Hoy sólo lee el listado de Noale" },
  { key: "rebalanceEnabled",     label: "Rebalanceo automático",              desc: "Activar el proceso de rebalanceo al cancelar turnos" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Reset de métricas por empresa (DESTRUCTIVO). Preview obligatorio → confirmación
// escribiendo el NOMBRE EXACTO (el de turnos_db, que puede diferir del de WAIA →
// por eso se compara contra preview.companyName) → execute. El backend TurnosIA
// hace el backup server-side + el borrado transaccional + invalida el caché.
// ─────────────────────────────────────────────────────────────────────────────
const RESET_LABELS = {
  appointments: "Turnos",
  appointmentAuditLogs: "Historial de turnos (auditoría)",
  reminderLogs: "Recordatorios",
  rebalanceSequences: "Secuencias de rebalanceo",
  escalationCases: "Casos derivados",
  waitingLists: "Lista de espera",
  botContexts: "Contextos del bot (pending/avisos)",
};

const MetricsResetSection = ({ company }) => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [preview, setPreview] = useState(null); // { companyName, from, to, includesFuture, counts, total }
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);

  // Cambiar el rango invalida la preview → obliga a re-previsualizar antes de borrar.
  const resetPreview = () => { setPreview(null); setConfirmName(""); setResult(null); };

  const loadPreview = async () => {
    if (!company) return;
    setLoadingPreview(true);
    setResult(null);
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const { data } = await api.get(
        `/superadmin/turnos/companies/${company.id}/metrics-reset/preview`,
        { params }
      );
      setPreview(data);
      setConfirmName("");
    } catch (e) {
      toast.error(e?.response?.data?.error || "No se pudo calcular la vista previa");
    } finally {
      setLoadingPreview(false);
    }
  };

  const nameMatches = preview && confirmName.trim() === String(preview.companyName || "").trim();

  const doReset = async () => {
    if (!company || !preview || !nameMatches) return;
    setExecuting(true);
    try {
      const { data } = await api.post(
        `/superadmin/turnos/companies/${company.id}/metrics-reset`,
        { from: from || null, to: to || null, confirmName: confirmName.trim() }
      );
      setResult(data);
      setPreview(null);
      setConfirmName("");
      // Un borrado de cero filas NO es "listo": se dice explícito.
      if ((data?.total || 0) === 0) toast.warning("No se encontraron datos en ese rango — no se borró nada");
      else toast.success(`Reset hecho: ${data.total} registros borrados — backup guardado en el servidor`);
    } catch (e) {
      toast.error(e?.response?.data?.error || "El reset falló (no se borró nada)");
    } finally {
      setExecuting(false);
    }
  };

  const totalToDelete = preview
    ? Object.values(preview.counts || {}).reduce((a, b) => a + b, 0) - (preview.counts?.futureAppointments || 0)
    : 0;

  return (
    <Paper
      variant="outlined"
      style={{ padding: "14px 16px", borderRadius: 8, border: "1px solid #e0a3a3", background: "rgba(211,47,47,0.04)" }}
    >
      <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#b71c1c", marginBottom: 6 }}>
        ⚠ Reset de métricas (borra datos operativos)
      </Typography>
      <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
        Deja a la empresa en cero para el rango elegido: borra turnos, recordatorios, rebalanceos,
        casos derivados, lista de espera y contextos del bot. <strong>NO toca</strong> profesionales,
        servicios, horarios, salas, plantillas, vocabulario, tokens ni flags. Antes de borrar se
        guarda un backup en el servidor. Irreversible desde el panel.
      </Typography>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <TextField
          label="Desde"
          helperText="Vacío = sin límite"
          type="date"
          size="small"
          value={from}
          onChange={(e) => { const v = e.target.value; setFrom(v); resetPreview(); }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Hasta"
          helperText="Vacío = sin límite (incluye futuros)"
          type="date"
          size="small"
          value={to}
          onChange={(e) => { const v = e.target.value; setTo(v); resetPreview(); }}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="outlined" onClick={loadPreview} disabled={loadingPreview || executing}>
          {loadingPreview ? <CircularProgress size={18} /> : "Vista previa"}
        </Button>
      </div>

      {preview && (
        <Paper variant="outlined" style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 12 }}>
          <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 6 }}>
            Se van a borrar {totalToDelete} registros
            {preview.from ? ` desde ${preview.from}` : " desde siempre"}
            {preview.to ? ` hasta ${preview.to}` : " sin límite de fecha (incluye futuros)"}:
          </Typography>
          {Object.keys(RESET_LABELS).map((k) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "1px 0" }}>
              <span style={{ color: "#555" }}>{RESET_LABELS[k]}</span>
              <span style={{ fontWeight: 600 }}>{preview.counts?.[k] ?? 0}</span>
            </div>
          ))}
          {preview.includesFuture && (preview.counts?.futureAppointments || 0) > 0 && (
            <Typography variant="caption" style={{ display: "block", marginTop: 8, color: "#b71c1c", fontWeight: 600 }}>
              ⚠ El rango incluye fechas futuras: se borran {preview.counts?.futureAppointments || 0} turnos
              FUTUROS (podrían ser reservas reales).
            </Typography>
          )}
          {totalToDelete === 0 && (
            <Typography variant="body2" style={{ display: "block", marginTop: 8, color: "#b71c1c", fontWeight: 600 }}>
              No se encontraron datos en este rango — no hay nada que borrar.
            </Typography>
          )}

          <div style={{ marginTop: 14 }}>
            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
              Para confirmar, escribí el nombre exacto de la empresa:{" "}
              <strong>{preview.companyName}</strong>
            </Typography>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder={preview.companyName}
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                style={{ minWidth: 260 }}
                autoComplete="off"
              />
              <Button
                variant="contained"
                onClick={doReset}
                disabled={!nameMatches || executing || totalToDelete === 0}
                style={nameMatches && totalToDelete > 0 ? { background: "#b71c1c", color: "#fff" } : undefined}
              >
                {executing ? <CircularProgress size={18} /> : "Resetear métricas"}
              </Button>
            </div>
          </div>
        </Paper>
      )}

      {result && (
        <Paper variant="outlined" style={{ padding: "10px 14px", borderRadius: 8 }}>
          {(result.total || 0) === 0 ? (
            <Typography variant="body2" style={{ fontWeight: 700, color: "#b71c1c", marginBottom: 6 }}>
              ⚠ No se encontraron datos en ese rango — no se borró nada.
            </Typography>
          ) : (
            <Typography variant="body2" style={{ fontWeight: 700, color: "#1b5e20", marginBottom: 6 }}>
              ✓ Reset hecho: {result.total} registros borrados
              {result.from ? ` desde ${result.from}` : " desde siempre"}
              {result.to ? ` hasta ${result.to}` : " sin límite de fecha"}.
            </Typography>
          )}
          {Object.keys(RESET_LABELS).filter((k) => k in (result.deleted || {})).map((k) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "1px 0" }}>
              <span style={{ color: "#555" }}>{RESET_LABELS[k]}</span>
              <span style={{ fontWeight: 600 }}>
                {result.deleted[k]} borrados
                {result.remaining ? ` · quedan ${result.remaining[k] ?? 0}` : ""}
              </span>
            </div>
          ))}
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 6 }}>
            Backup: <code>{result.backupPath}</code>
          </Typography>
        </Paper>
      )}
    </Paper>
  );
};

// Tab 1 — Empresas
// ─────────────────────────────────────────────────────────────────────────────
const EmpresasTab = () => {
  const classes = useStyles();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedSettings, setSelectedSettings] = useState([]);
  const [turnosConfig, setTurnosConfig] = useState(null);
  const [featureForm, setFeatureForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rubroToApply, setRubroToApply] = useState("");
  const [applyingRubro, setApplyingRubro] = useState(false);
  const [rubroTemplates, setRubroTemplates] = useState([]);
  const [planValue, setPlanValue] = useState("turnos");
  const [savedPlan, setSavedPlan] = useState("turnos");
  const [savingPlan, setSavingPlan] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  // Webhook n8n de TurnosIA (nivel 1) — edición superadmin (movido desde el panel admin).
  const [webhookCfg, setWebhookCfg] = useState(null); // { n8nWebhookUrl, outgoingWhatsappId, suggestedUrl, lines, provisioned }
  // Interruptor de la IA de la empresa — SOLO LECTURA acá. Quien lo prende y
  // apaga es el admin desde TurnosIA › Ajustes (ahí queda registrado quién fue).
  // Se muestra al lado del webhook porque es la pregunta que sigue cuando una
  // empresa avisa "el bot no contesta": el webhook puede estar perfecto y la IA
  // apagada a propósito. ⛔ Apagada NO apaga los recordatorios.
  const [iaInfo, setIaInfo] = useState(null); // { enabled, changedBy, changedAt }
  const [webhookUrlInput, setWebhookUrlInput] = useState("");
  const [webhookOutgoing, setWebhookOutgoing] = useState("");
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);
  // feat_263 — Set estándar de plantillas Meta de TurnosIA (vista + seed).
  const [stdTemplates, setStdTemplates] = useState(null); // { applicable, reason, whatsappId, language, items[] }
  const [stdLoading, setStdLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // FASE 1 — edición de prosa ANTES de crear (variables bloqueadas).
  const [seedDialog, setSeedDialog] = useState(null); // { items:[{name, category, language, bodyText, bodyExamples, varOrder}], whatsappId }
  const [seedEdits, setSeedEdits] = useState({});     // { [name]: bodyTextEditado }
  const [seedSubmitting, setSeedSubmitting] = useState(false);

  // FASE 2 — override manual mensaje→plantilla (superadmin).
  const [ovData, setOvData] = useState(null);         // { kinds, overrides, whatsappId }
  const [ovApproved, setOvApproved] = useState(null); // { applicable, templates, ... }
  const [ovSaving, setOvSaving] = useState({});

  const loadCompanies = useCallback(async () => {
    try {
      const { data: allCompanies } = await api.get("/companies");
      // Fetch settings for all companies in parallel
      const withSettings = await Promise.all(
        allCompanies.map(async (c) => {
          try {
            const { data: settings } = await api.get(`/companies/${c.id}/settings`);
            const turnosEnabled =
              settings.find((s) => s.key === "turnosIAEnabled")?.value === "true";
            return { ...c, settings, turnosEnabled };
          } catch {
            return { ...c, settings: [], turnosEnabled: false };
          }
        })
      );
      // Show all companies, highlighting which have turnosIA enabled
      setCompanies(withSettings);
    } catch (err) {
      toast.error("Error al cargar empresas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // Catálogo real de plantillas de rubro — para el selector "Plantilla de rubro"
  // del diálogo de empresa (antes hardcodeado a generico/odontologia/taller). Se
  // filtra por el idioma de la empresa al renderizar.
  useEffect(() => {
    api.get("/superadmin/turnos/rubro-templates")
      .then(({ data }) => setRubroTemplates(Array.isArray(data?.templates) ? data.templates : []))
      .catch(() => setRubroTemplates([]));
  }, []);

  // Toggle TurnosIA habilitado — MISMO backend que el switch de Módulos de WAIA
  // (setting turnosIAEnabled + provisioning idempotente de turnos_db al habilitar).
  const handleToggleEnabled = async (company, enabled) => {
    setTogglingId(company.id);
    try {
      await api.put(`/superadmin/turnos/company-enabled/${company.id}`, { enabled });
      toast.success(enabled ? "TurnosIA habilitado" : "TurnosIA deshabilitado");
      await loadCompanies();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error al cambiar el estado de TurnosIA");
    } finally {
      setTogglingId(null);
    }
  };

  const handleViewConfig = async (company) => {
    setSelectedCompany(company);
    setSelectedSettings(company.settings || []);
    setTurnosConfig(null);
    setFeatureForm({});
    setRubroToApply("");
    setWebhookCfg(null);
    setWebhookUrlInput("");
    setWebhookOutgoing("");
    setIaInfo(null);
    setDialogOpen(true);
    // Cargar config de TurnosIA para esa empresa
    if (company.turnosEnabled) {
      setSettingsLoading(true);
      try {
        const { data: allConfigs } = await api.get("/superadmin/turnos/companies");
        const cfg = Array.isArray(allConfigs) ? allConfigs.find(c => c.id === company.id) : null;
        if (cfg) {
          setTurnosConfig(cfg);
          const form = {};
          FEATURE_DEFS.forEach(f => { form[f.key] = Boolean(cfg[f.key]); });
          form.timezone = cfg.timezone || "America/Argentina/Cordoba";
          form.locale = cfg.locale || "es-AR";
          form.companySlug = cfg.companySlug || "";
          form.rebalanceTimerMinutes = cfg.rebalanceTimerMinutes || 30;
          // Enfriamiento post-reserva: null/ausente → default 120; 0 = desactivado.
          form.reminderCooldownMinutes =
            cfg.reminderCooldownMinutes === null || cfg.reminderCooldownMinutes === undefined
              ? 120
              : cfg.reminderCooldownMinutes;
          // Duración por defecto del turno: la que aplica el motor de slots
          // cuando la consulta no trae servicio (y la base del cálculo de
          // adelanto/atraso). Editable acá y en Ajustes del admin.
          form.defaultAppointmentDurationMinutes = cfg.defaultAppointmentDurationMinutes || 30;
          // Ventana de reserva (techo valor+unidad, piso) + umbral de autoreserva.
          // El listado ya los traía en COMPANY_FIELDS; faltaba dónde editarlos.
          // Los NULLABLES van a "" en el form y vuelven a null al guardar.
          form.advancementWindowDays = cfg.advancementWindowDays || 30;
          form.advancementWindowUnit = cfg.advancementWindowUnit || "days";
          form.minBookingLeadDays =
            cfg.minBookingLeadDays === null || cfg.minBookingLeadDays === undefined
              ? ""
              : cfg.minBookingLeadDays;
          form.selfBookingConfirmSkipDays =
            cfg.selfBookingConfirmSkipDays === null || cfg.selfBookingConfirmSkipDays === undefined
              ? ""
              : cfg.selfBookingConfirmSkipDays;
          form.botSlotLimitMax =
            cfg.botSlotLimitMax === null || cfg.botSlotLimitMax === undefined
              ? ""
              : cfg.botSlotLimitMax;
          // Preselecciona el rubro vigente de la empresa (antes arrancaba vacío →
          // el desplegable no reflejaba el rubro ya aplicado al reabrir).
          setRubroToApply(cfg.rubroTemplate || "");
          const plan = cfg.plan || "turnos";
          setPlanValue(plan);
          setSavedPlan(plan);
          // Interruptor de la IA — EDITABLE acá desde feat_368 (antes solo lectura).
          // Va en el form porque se guarda con el mismo PATCH que el resto; el
          // backend es idempotente, así que guardar sin cambiarlo no reescribe la
          // auditoría. `iaInfo` queda solo para el cartel de estado + último cambio.
          form.iaEnabled = cfg.iaEnabled !== false;
          // feat_369 — los dos GATES DE SUPERADMIN + el switch de recordatorios de
          // la empresa. Los `*SuperadminEnabled` sólo se cambian desde acá: el
          // PATCH del admin de la empresa los rechaza con 403.
          // EFECTIVO = superadmin AND empresa, en los dos casos.
          form.iaSuperadminEnabled = cfg.iaSuperadminEnabled !== false;
          form.remindersEnabled = cfg.remindersEnabled !== false;
          form.remindersSuperadminEnabled = cfg.remindersSuperadminEnabled !== false;
          // ⚠ El setState va DESPUÉS de terminar de armar el form: antes se
          // llamaba arriba y los campos siguientes llegaban por mutación del
          // mismo objeto — funcionaba de casualidad.
          setFeatureForm(form);
          setIaInfo({
            enabled: cfg.iaEnabled !== false,
            changedBy: cfg.iaChangedBy ?? null,
            changedAt: cfg.iaChangedAt ?? null,
            superadminEnabled: cfg.iaSuperadminEnabled !== false,
            superadminChangedAt: cfg.iaSuperadminChangedAt ?? null,
            remindersEnabled: cfg.remindersEnabled !== false,
            remindersChangedBy: cfg.remindersChangedBy ?? null,
            remindersChangedAt: cfg.remindersChangedAt ?? null,
            remindersSuperadminEnabled: cfg.remindersSuperadminEnabled !== false,
            remindersSuperadminChangedAt: cfg.remindersSuperadminChangedAt ?? null
          });
        }
      } catch (_) {}
      setSettingsLoading(false);
      // Webhook n8n (nivel 1) + estado de líneas — endpoint dedicado con guardrails.
      setWebhookLoading(true);
      try {
        const { data: wh } = await api.get(`/superadmin/turnos/companies/${company.id}/webhook`);
        setWebhookCfg(wh);
        // Precarga: URL actual, o el valor sugerido si está vacía (editable).
        setWebhookUrlInput(wh?.n8nWebhookUrl || wh?.suggestedUrl || "");
        setWebhookOutgoing(wh?.outgoingWhatsappId ? String(wh.outgoingWhatsappId) : "");
      } catch (_) {}
      setWebhookLoading(false);
      // Set estándar de plantillas Meta + estado (read-only).
      setStdTemplates(null);
      setStdLoading(true);
      try {
        const { data: st } = await api.get(`/superadmin/turnos/companies/${company.id}/standard-templates`);
        setStdTemplates(st);
      } catch (_) {}
      setStdLoading(false);
      // FASE 2 — override manual mensaje→plantilla + plantillas aprobadas de la línea.
      setOvData(null);
      setOvApproved(null);
      try {
        const { data: ov } = await api.get(`/superadmin/turnos/companies/${company.id}/template-overrides`);
        setOvData(ov);
      } catch (_) {}
      try {
        const { data: ap } = await api.get(`/superadmin/turnos/companies/${company.id}/approved-templates`);
        setOvApproved(ap);
      } catch (_) { setOvApproved({ applicable: false }); }
    }
  };

  // Recarga solo la vista del set estándar (tras un seed).
  const reloadStdTemplates = async (companyId) => {
    setStdLoading(true);
    try {
      const { data: st } = await api.get(`/superadmin/turnos/companies/${companyId}/standard-templates`);
      setStdTemplates(st);
    } catch (_) {}
    setStdLoading(false);
  };

  // feat_263 — Seed del set estándar. Previsualiza con dryRun (sin submit a Meta),
  // pide confirmación explícita, y recién ahí hace el submit REAL a Meta.
  const handleSeedTemplates = async () => {
    if (!selectedCompany) return;
    setSeeding(true);
    try {
      // 1) Dry-run: qué se crearía (NO toca Meta).
      const { data: dry } = await api.post(
        `/superadmin/turnos/companies/${selectedCompany.id}/standard-templates`,
        { dryRun: true }
      );
      if (!dry.applicable) {
        toast.info(
          dry.reason === "qr_line"
            ? "La línea de TurnosIA es QR: manda texto libre, no requiere plantillas."
            : "La empresa no tiene una línea Meta de TurnosIA configurada."
        );
        setSeeding(false);
        return;
      }
      const toCreate = (dry.items || []).filter((i) => i.action === "would_create" && i.payload);
      if (toCreate.length === 0) {
        toast.success("El set ya está completo: no falta ninguna plantilla.");
        setSeeding(false);
        return;
      }
      // 2) Abrir el editor de prosa (variables bloqueadas). El submit real se hace
      //    desde el diálogo (handleSeedConfirm). No se manda nada a Meta acá.
      setSeedDialog({
        whatsappId: dry.whatsappId,
        language: dry.language,
        items: toCreate.map((i) => i.payload),
      });
      setSeedEdits({});
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error al previsualizar el set de plantillas");
    }
    setSeeding(false);
  };

  // Cuenta las variables {{n}} de un body (para bloquear la edición de variables).
  const varNumsOf = (text) => {
    const m = (text || "").match(/\{\{\s*(\d+)\s*\}\}/g) || [];
    return [...new Set(m.map((x) => Number(x.replace(/\D/g, ""))))].sort((a, b) => a - b);
  };
  // Un body editado es válido si conserva {{1}}..{{n}} correlativas y el MISMO conteo.
  const editedBodyValid = (edited, varCount) => {
    const nums = varNumsOf(edited);
    return nums.length === varCount && nums.every((n, i) => n === i + 1);
  };

  // FASE 1 — submit REAL a Meta con la prosa editada. ⚠ Manda plantillas a Meta:
  // en esta corrida de dev NO se ejecuta (lo dispara Mariano). El código queda listo.
  const handleSeedConfirm = async () => {
    if (!selectedCompany || !seedDialog) return;
    // Validación cliente: variables bloqueadas.
    const invalid = seedDialog.items.filter((it) => {
      const edited = seedEdits[it.name];
      return edited != null && !editedBodyValid(edited, it.varOrder.length);
    });
    if (invalid.length > 0) {
      toast.error(`Corregí las variables de: ${invalid.map((i) => i.name).join(", ")}. Deben quedar {{1}}..{{n}}.`);
      return;
    }
    const overrides = {};
    for (const it of seedDialog.items) {
      const edited = seedEdits[it.name];
      if (edited != null && edited.trim() && edited.trim() !== it.bodyText.trim()) {
        overrides[it.name] = { bodyText: edited };
      }
    }
    setSeedSubmitting(true);
    try {
      const { data: real } = await api.post(
        `/superadmin/turnos/companies/${selectedCompany.id}/standard-templates`,
        { dryRun: false, overrides }
      );
      const created = (real.items || []).filter((i) => i.action === "created").length;
      const errored = (real.items || []).filter((i) => i.action === "error");
      if (errored.length > 0) {
        toast.warning(`${created} creada(s); ${errored.length} con error (${errored.map((e) => `${e.name}: ${e.error || ""}`).join("; ")}).`);
      } else {
        toast.success(`${created} plantilla(s) enviada(s) a Meta (PENDING hasta aprobación).`);
      }
      setSeedDialog(null);
      setSeedEdits({});
      await reloadStdTemplates(selectedCompany.id);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error al crear el set de plantillas");
    }
    setSeedSubmitting(false);
  };

  // FASE 2 — guarda un override manual (superadmin).
  const handleOvSave = async (kind, { templateName, variableCount }) => {
    if (!selectedCompany) return;
    setOvSaving((prev) => ({ ...prev, [kind]: true }));
    try {
      const { data } = await api.patch(
        `/superadmin/turnos/companies/${selectedCompany.id}/template-overrides`,
        { kind, templateName, variableCount }
      );
      setOvData((prev) => (prev ? { ...prev, overrides: data.overrides, kinds: data.kinds } : prev));
    } catch (err) {
      toast.error(err?.response?.data?.error || "No se pudo guardar el mapeo");
    }
    setOvSaving((prev) => ({ ...prev, [kind]: false }));
  };

  // Guarda el webhook n8n + línea de TurnosIA vía el endpoint con guardrails.
  const handleSaveWebhook = async () => {
    if (!selectedCompany) return;
    setSavingWebhook(true);
    try {
      const { data } = await api.patch(
        `/superadmin/turnos/companies/${selectedCompany.id}/webhook`,
        {
          n8nWebhookUrl: webhookUrlInput.trim(),
          outgoingWhatsappId: webhookOutgoing === "" ? null : Number(webhookOutgoing)
        }
      );
      setWebhookCfg(data);
      setWebhookUrlInput(data?.n8nWebhookUrl || "");
      setWebhookOutgoing(data?.outgoingWhatsappId ? String(data.outgoingWhatsappId) : "");
      toast.success("Webhook de TurnosIA actualizado");
    } catch (err) {
      // El guardrail server-side viaja en err.response.data.error (texto legible).
      toast.error(err?.response?.data?.error || "Error al guardar el webhook");
    }
    setSavingWebhook(false);
  };

  const handleSaveFeatures = async () => {
    if (!selectedCompany) return;
    setSaving(true);
    try {
      // "" en un campo NULLABLE = borrar el valor, no mandar la cadena vacía
      // (Number("") es 0, que en el piso significa algo distinto: "sin piso"
      // guardado vs. ausencia de piso).
      const payload = { ...featureForm };
      ["minBookingLeadDays", "selfBookingConfirmSkipDays", "botSlotLimitMax"].forEach(k => {
        if (payload[k] === "" || payload[k] === undefined) payload[k] = null;
      });
      await api.patch(`/superadmin/turnos/companies/${selectedCompany.id}`, payload);
      toast.success("Configuración actualizada");
      setDialogOpen(false);
    } catch (err) {
      // El backend rechaza el slug duplicado con 400 + mensaje legible.
      toast.error(err?.response?.data?.error || "Error al guardar");
    }
    setSaving(false);
  };

  const handleSavePlan = async () => {
    if (!selectedCompany || planValue === savedPlan) return;
    setSavingPlan(true);
    try {
      const { data } = await api.patch(`/superadmin/turnos/companies/${selectedCompany.id}/plan`, { plan: planValue });
      const applied = data?.plan || planValue;
      setSavedPlan(applied);
      setPlanValue(applied);
      setTurnosConfig(prev => (prev ? { ...prev, plan: applied } : prev));
      const modos = Array.isArray(data?.allowedModes) ? data.allowedModes.join(", ") : "";
      toast.success(`Plan "${applied}" asignado${modos ? ` — modos: ${modos}` : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error al asignar el plan");
    }
    setSavingPlan(false);
  };

  const handleApplyRubro = async () => {
    if (!selectedCompany || !rubroToApply) return;
    setApplyingRubro(true);
    try {
      const { data } = await api.post(`/superadmin/turnos/companies/${selectedCompany.id}/apply-template`, { template: rubroToApply });
      toast.success(`Plantilla "${rubroToApply}" aplicada (${data?.specialtiesSeeded ?? 0} especialidades, ${data?.customFieldsCreated ?? 0} campos)`);
      setTurnosConfig(prev => (prev ? { ...prev, rubroTemplate: rubroToApply } : prev));
      // Mantener el selector en el rubro recién aplicado (antes se vaciaba → el
      // desplegable quedaba en placeholder aunque "Rubro actual" ya reflejaba el nuevo).
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error al aplicar plantilla");
    }
    setApplyingRubro(false);
  };

  const turnosCompanies = companies.filter((c) => c.turnosEnabled);
  const otherCompanies = companies.filter((c) => !c.turnosEnabled);

  if (loading) {
    return <CircularProgress size={32} style={{ display: "block", margin: "40px auto" }} />;
  }

  return (
    <div>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
        Empresas con módulo TurnosIA activo:{" "}
        <strong>{turnosCompanies.length}</strong> de {companies.length}
      </Typography>

      {turnosCompanies.length === 0 && (
        <Paper className={classes.section} style={{ padding: 40, textAlign: "center" }}>
          <CalendarTodayIcon style={{ fontSize: 48, color: "#bdbdbd", marginBottom: 12 }} />
          <Typography color="textSecondary">
            Ninguna empresa tiene TurnosIA habilitado actualmente.
          </Typography>
        </Paper>
      )}

      {turnosCompanies.length > 0 && (
        <Paper className={classes.tableCard}>
          <Typography variant="subtitle1" className={classes.tableTitle}>
            Con TurnosIA
          </Typography>
          <Divider />
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Empresa</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>TurnosIA</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {turnosCompanies.map((c) => (
                <TableRow key={c.id}>
                  <TableCell><strong>{c.name}</strong></TableCell>
                  <TableCell>{c.id}</TableCell>
                  <TableCell>
                    {togglingId === c.id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Switch
                        checked={!!c.turnosEnabled}
                        color="primary"
                        size="small"
                        onChange={(e) => handleToggleEnabled(c, e.target.checked)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={c.active ? "Activa" : "Inactiva"}
                      size="small"
                      className={classes.statusChip}
                      style={{
                        backgroundColor: c.active ? "#e3f2fd" : "#fce4ec",
                        color: c.active ? "#1565c0" : "#c62828",
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" onClick={() => handleViewConfig(c)}>
                      Ver config
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {otherCompanies.length > 0 && (
        <Paper className={classes.tableCard}>
          <Typography variant="subtitle1" className={classes.tableTitle} color="textSecondary">
            Sin TurnosIA ({otherCompanies.length})
          </Typography>
          <Divider />
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Empresa</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>TurnosIA</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {otherCompanies.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>{c.id}</TableCell>
                  <TableCell>
                    {togglingId === c.id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Switch
                        checked={!!c.turnosEnabled}
                        color="primary"
                        size="small"
                        onChange={(e) => handleToggleEnabled(c, e.target.checked)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={c.active ? "Activa" : "Inactiva"}
                      size="small"
                      className={classes.statusChip}
                      style={{
                        backgroundColor: c.active ? "#e3f2fd" : "#fce4ec",
                        color: c.active ? "#1565c0" : "#c62828",
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="text" onClick={() => handleViewConfig(c)}>
                      Ver config
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Dialog de configuración con features editables */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          TurnosIA — {selectedCompany?.name} (ID: {selectedCompany?.id})
        </DialogTitle>
        <DialogContent dividers>
          {/* Sección features */}
          {selectedCompany?.turnosEnabled && (
            <>
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                Features habilitados
              </Typography>
              {settingsLoading ? (
                <CircularProgress size={24} style={{ display: "block", margin: "16px auto" }} />
              ) : (
                <Paper variant="outlined" style={{ padding: 4, marginBottom: 20, borderRadius: 8 }}>
                  {FEATURE_DEFS.map((f, i) => (
                    <div key={f.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 12px", borderBottom: i === FEATURE_DEFS.length - 1 ? "none" : "1px solid rgba(128,128,128,0.15)" }}>
                      <div>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>{f.label}</Typography>
                        <Typography variant="caption" color="textSecondary">{f.desc}</Typography>
                      </div>
                      <Switch
                        checked={!!featureForm[f.key]}
                        onChange={e => {
                          const v = e.target.checked;
                          setFeatureForm(prev => ({ ...prev, [f.key]: v }));
                        }}
                        color="primary"
                        size="small"
                      />
                    </div>
                  ))}
                </Paper>
              )}
              {/* Slug + timer de rebalanceo — editable SOLO superadmin (infra/provisioning).
                  Movido desde el panel admin (feat_260). El switch de rebalanceo
                  automático (rebalanceEnabled) está arriba en Features. */}
              {turnosConfig && (
                <>
                  <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                    Slug y rebalanceo
                  </Typography>
                  <Paper variant="outlined" style={{ padding: "14px 16px", marginBottom: 16, borderRadius: 8 }}>
                    <TextField
                      label="Slug de la empresa"
                      variant="outlined" size="small" fullWidth
                      value={featureForm.companySlug || ""}
                      onChange={e => { const v = e.target.value; setFeatureForm(prev => ({ ...prev, companySlug: v })); }}
                      placeholder="mi-empresa"
                      helperText="⚠ Cambiar el slug afecta las URLs de la empresa (links de sala de espera). Solo minúsculas, números y guiones; debe ser único."
                      style={{ marginBottom: 12 }}
                    />
                    <TextField
                      label="Timer de rebalanceo (minutos)"
                      type="number"
                      variant="outlined" size="small"
                      value={featureForm.rebalanceTimerMinutes || 30}
                      onChange={e => { const v = Number(e.target.value); setFeatureForm(prev => ({ ...prev, rebalanceTimerMinutes: v })); }}
                      inputProps={{ min: 5, max: 1440 }}
                      helperText="Espera antes de ofrecer el turno liberado al siguiente en la lista."
                      style={{ minWidth: 260, marginRight: 16 }}
                    />
                    <TextField
                      label="Enfriamiento de recordatorios (minutos)"
                      type="number"
                      variant="outlined" size="small"
                      value={featureForm.reminderCooldownMinutes ?? 120}
                      onChange={e => { const v = Number(e.target.value); setFeatureForm(prev => ({ ...prev, reminderCooldownMinutes: v })); }}
                      inputProps={{ min: 0, max: 10080 }}
                      helperText="No enviar recordatorios cuya hora de envío caiga dentro de estos minutos desde que se creó el turno. Default 120. 0 = desactivado (igual nunca se disparan pasos ya vencidos al reservar)."
                      style={{ minWidth: 260 }}
                    />
                    <TextField
                      label="Duración por defecto del turno (minutos)"
                      type="number"
                      variant="outlined" size="small"
                      value={featureForm.defaultAppointmentDurationMinutes ?? 30}
                      onChange={e => { const v = Number(e.target.value); setFeatureForm(prev => ({ ...prev, defaultAppointmentDurationMinutes: v })); }}
                      inputProps={{ min: 5, max: 240 }}
                      helperText="Duración que aplica el motor de slots cuando la consulta NO indica un servicio. Orden: la del servicio elegido → esta → 30 min. Una empresa de turnos largos que la deje en 30 verá el bot ofreciendo horarios que la reserva después rechaza."
                      style={{ minWidth: 260, marginTop: 12 }}
                    />
                    {/* Ventana de reserva: techo (valor + unidad) y piso, + el
                        umbral de autoreserva. Los mismos tres/cuatro campos que
                        el admin ve en Ajustes → acá para poder dejar la empresa
                        configurada al entregarla. */}
                    <Divider style={{ margin: "16px 0 12px" }} />
                    <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 8 }}>
                      Ventana de reserva
                    </Typography>
                    <TextField
                      label="Anticipación máxima para reservar"
                      type="number"
                      variant="outlined" size="small"
                      value={featureForm.advancementWindowDays ?? 30}
                      onChange={e => { const v = Number(e.target.value); setFeatureForm(prev => ({ ...prev, advancementWindowDays: v })); }}
                      inputProps={{
                        min: 1,
                        max: featureForm.advancementWindowUnit === "months" ? 24 : 180
                      }}
                      helperText={`Techo de la agenda: hasta dónde hacia adelante se puede dar un turno. Vale para el panel y para el bot. Máximo ${featureForm.advancementWindowUnit === "months" ? 24 : 180}.`}
                      style={{ minWidth: 260, marginRight: 16 }}
                    />
                    <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
                      <InputLabel>Unidad</InputLabel>
                      <Select
                        label="Unidad"
                        value={featureForm.advancementWindowUnit || "days"}
                        onChange={e => { const v = e.target.value; setFeatureForm(prev => ({ ...prev, advancementWindowUnit: v })); }}
                      >
                        <MenuItem value="days">días</MenuItem>
                        <MenuItem value="months">meses</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Anticipación mínima (días, solo bot)"
                      type="number"
                      variant="outlined" size="small"
                      value={featureForm.minBookingLeadDays ?? ""}
                      onChange={e => { const v = e.target.value; setFeatureForm(prev => ({ ...prev, minBookingLeadDays: v })); }}
                      inputProps={{ min: 0, max: 365 }}
                      placeholder="0"
                      helperText="Piso: los próximos N días quedan para quien llama o se acerca — el bot NO los ofrece, el panel sí los puede agendar. Vacío/0 = sin piso."
                      style={{ minWidth: 260, marginTop: 12, marginRight: 16 }}
                    />
                    <TextField
                      label="No pedir confirmación si la sacó el cliente (días)"
                      type="number"
                      variant="outlined" size="small"
                      value={featureForm.selfBookingConfirmSkipDays ?? ""}
                      onChange={e => { const v = e.target.value; setFeatureForm(prev => ({ ...prev, selfBookingConfirmSkipDays: v })); }}
                      inputProps={{ min: 0, max: 365 }}
                      placeholder="2"
                      helperText="Si el turno lo sacó el propio cliente por WhatsApp con menos de N días de anticipación, se saltea el pedido de confirmación (ya lo confirmó al reservar). Vacío = 2. 0 = nunca saltear. NO aplica a lo que carga la secretaria."
                      style={{ minWidth: 260, marginTop: 12 }}
                    />
                    <TextField
                      label="Horarios que recibe el bot por consulta"
                      type="number"
                      variant="outlined" size="small"
                      value={featureForm.botSlotLimitMax ?? ""}
                      onChange={e => { const v = e.target.value; setFeatureForm(prev => ({ ...prev, botSlotLimitMax: v })); }}
                      inputProps={{ min: 1, max: 200 }}
                      placeholder="20"
                      helperText="Techo de slots que devuelven /bot/slots y /bot/professionals/:id/slots por consulta. Vacío = 20 (el default histórico). Con varias sucursales el 20 corta la tarde: los primeros horarios de la mañana de TODAS las sucursales se comen el cupo. Subirlo cuesta tokens del LLM."
                      style={{ minWidth: 260, marginTop: 12, marginRight: 16 }}
                    />

                    {/* Diagnóstico (solo lectura): capacidad simultánea instalada.
                        Se edita por profesional, en el panel de la empresa. */}
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
                      Capacidad instalada:{" "}
                      <strong>{turnosConfig?.capacitySum ?? 0}</strong>{" "}
                      turnos simultáneos en {turnosConfig?.activeProfessionals ?? 0} recurso(s) activo(s)
                      {(turnosConfig?.capacitySum ?? 0) === (turnosConfig?.activeProfessionals ?? 0)
                        ? " — todos en 1 (atención de a uno)."
                        : " — hay recursos con atención simultánea."}
                    </Typography>

                    {/* Diagnóstico de precios (SOLO LECTURA). El superadmin no
                        edita precios de un cliente; esto responde "¿por qué el
                        bot no da precios?" sin entrar a la empresa. */}
                    {turnosConfig?.featurePrecios && (
                      <Typography
                        variant="body2"
                        style={{
                          marginTop: 12,
                          color: turnosConfig?.priceList?.expired ? "#c62828" : "rgba(128,128,128,0.95)"
                        }}
                      >
                        Lista de precios:{" "}
                        {turnosConfig?.priceList ? (
                          <>
                            <strong>{turnosConfig.priceList.itemCount}</strong> precios,
                            vigente desde {turnosConfig.priceList.effectiveFrom || "—"}
                            {turnosConfig.priceList.ageDays !== null
                              ? ` (${turnosConfig.priceList.ageDays} días)`
                              : ""}
                            {turnosConfig.priceList.expired
                              ? ` — ⚠ VENCIDA (límite ${turnosConfig.priceList.validityDays} días): el bot no la va a usar.`
                              : turnosConfig.priceList.validityDays === null
                                ? " — sin control de vencimiento."
                                : ` — vigente (límite ${turnosConfig.priceList.validityDays} días).`}
                          </>
                        ) : (
                          "el feature está encendido pero la empresa todavía no importó ninguna lista."
                        )}
                      </Typography>
                    )}
                  </Paper>
                </>
              )}
              {/* Webhook n8n de TurnosIA (nivel 1) — editable SOLO superadmin.
                  Movido desde el panel admin. Guardrails server-side impiden
                  robarle el inbound a otro bot (incidente co11/SofiAutos). */}
              {turnosConfig && (
                <>
                  <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8, marginTop: 12 }}>
                    Webhook n8n de TurnosIA
                  </Typography>
                  {iaInfo && (() => {
                    // Los switches leen del FORM (lo editable); `iaInfo` solo aporta
                    // la auditoría del último cambio, que no se refresca hasta recargar.
                    //
                    // DOS NIVELES POR PRODUCTO (feat_369):
                    //   IA:            iaEnabled (cliente) AND iaSuperadminEnabled
                    //   Recordatorios: remindersEnabled (cliente) AND remindersSuperadminEnabled
                    // Los `*Superadmin*` SOLO se cambian acá — el PATCH del admin de
                    // la empresa los rechaza con 403, y en su panel el control se ve
                    // deshabilitado con el motivo (no se esconde).
                    //
                    // ⛔ Son interruptores DISTINTOS porque apagan cosas distintas.
                    // Apagar la IA no toca los recordatorios (no pasan por n8n), y
                    // apagar los recordatorios deja a ITV sin producto: usan TurnosIA
                    // como sistema de avisos con la IA apagada de forma permanente.
                    const iaCliente = featureForm.iaEnabled !== false;
                    const iaSuper = featureForm.iaSuperadminEnabled !== false;
                    const remCliente = featureForm.remindersEnabled !== false;
                    const remSuper = featureForm.remindersSuperadminEnabled !== false;

                    const bloque = ({ titulo, onTexto, offTexto, superOffTexto, ayuda, cliente, superadmin, setCliente, setSuper, cambio }) => {
                      const efectivo = cliente && superadmin;
                      const color = !superadmin ? "#9e9e9e" : (efectivo ? "#2e7d32" : "#ed6c02");
                      return (
                        <Paper
                          variant="outlined"
                          style={{
                            padding: "10px 14px",
                            marginBottom: 12,
                            borderRadius: 8,
                            borderLeft: `4px solid ${color}`,
                            backgroundColor: !superadmin
                              ? "rgba(0,0,0,0.04)"
                              : (efectivo ? "transparent" : "rgba(237,108,2,0.08)")
                          }}
                        >
                          <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 6 }}>
                            {!superadmin ? superOffTexto : (efectivo ? onTexto : offTexto)}
                          </Typography>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                            <Typography variant="caption" style={{ fontWeight: 600 }}>
                              {titulo} — habilitado por SUPERADMIN
                            </Typography>
                            <Switch
                              checked={superadmin}
                              onChange={e => setSuper(e.target.checked)}
                              color="primary"
                              size="small"
                            />
                          </div>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                            <Typography variant="caption" color="textSecondary">
                              {titulo} — interruptor del CLIENTE (también lo maneja el admin desde Ajustes)
                            </Typography>
                            <Switch
                              checked={cliente}
                              onChange={e => setCliente(e.target.checked)}
                              color="primary"
                              size="small"
                            />
                          </div>
                          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
                            Efectivo = <strong>los dos en sí</strong>. Se guarda con <strong>Guardar</strong>,
                            igual que el resto de esta ficha. {ayuda}
                            {cambio ? ` Último cambio: ${cambio}.` : ""}
                          </Typography>
                        </Paper>
                      );
                    };

                    return (
                      <>
                        {bloque({
                          titulo: "Asistente",
                          onTexto: "IA prendida — el inbound de esta empresa se despacha al webhook.",
                          offTexto: "IA APAGADA por el cliente — el inbound NO se despacha al webhook.",
                          superOffTexto: "IA NO HABILITADA por superadmin — no despacha aunque el cliente la prenda.",
                          ayuda: "Con la IA apagada los recordatorios, las confirmaciones y las campañas siguen saliendo: no pasan por n8n.",
                          cliente: iaCliente,
                          superadmin: iaSuper,
                          setCliente: v => setFeatureForm(prev => ({ ...prev, iaEnabled: v })),
                          setSuper: v => setFeatureForm(prev => ({ ...prev, iaSuperadminEnabled: v })),
                          cambio: iaInfo.changedAt
                            ? `${new Date(iaInfo.changedAt).toLocaleString("es-AR")}${
                                iaInfo.changedBy ? ` — usuario ${iaInfo.changedBy}` : " — desde este panel"
                              }`
                            : ""
                        })}
                        {bloque({
                          titulo: "Avisos programados",
                          onTexto: "Recordatorios prendidos — los pasos programados se despachan.",
                          offTexto: "Recordatorios APAGADOS por el cliente — los pasos programados no salen.",
                          superOffTexto: "Recordatorios NO HABILITADOS por superadmin — no salen aunque el cliente los prenda.",
                          ayuda: "⛔ Apagarlos NO apaga la confirmación al crear un turno (la controla `notificar`) ni las campañas de WAIA. Se evalúa al despachar: apagar y volver a prender retoma la cola sin perder nada. ⛔ Para ITV (co20) los avisos SON el producto.",
                          cliente: remCliente,
                          superadmin: remSuper,
                          setCliente: v => setFeatureForm(prev => ({ ...prev, remindersEnabled: v })),
                          setSuper: v => setFeatureForm(prev => ({ ...prev, remindersSuperadminEnabled: v })),
                          cambio: iaInfo.remindersChangedAt
                            ? `${new Date(iaInfo.remindersChangedAt).toLocaleString("es-AR")}${
                                iaInfo.remindersChangedBy ? ` — usuario ${iaInfo.remindersChangedBy}` : " — desde este panel"
                              }`
                            : ""
                        })}
                      </>
                    );
                  })()}
                  <Paper variant="outlined" style={{ padding: "14px 16px", marginBottom: 16, borderRadius: 8 }}>
                    <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
                      URL a la que TurnosIA reenvía el inbound de <strong>su línea dedicada</strong> (nivel 1). El valor sugerido es la URL de TIA que usan las empresas TurnosIA; podés editarlo por empresa. Se escribe SOLO en la config de TurnosIA — nunca en el webhook de otra línea o de la empresa.
                    </Typography>
                    {webhookLoading ? (
                      <CircularProgress size={22} />
                    ) : (
                      <>
                        <TextField
                          label="URL webhook n8n (TurnosIA)"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={webhookUrlInput}
                          onChange={e => setWebhookUrlInput(e.target.value)}
                          placeholder={webhookCfg?.suggestedUrl || "https://n2.zzia.info/webhook/..."}
                          helperText={
                            webhookCfg && !webhookCfg.n8nWebhookUrl
                              ? "Sin configurar — se precargó el valor sugerido. Guardá para aplicarlo."
                              : "Sugerido: " + (webhookCfg?.suggestedUrl || "")
                          }
                          style={{ marginBottom: 12 }}
                        />
                        <FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 8 }}>
                          <InputLabel>Línea de TurnosIA</InputLabel>
                          <Select
                            value={webhookOutgoing}
                            label="Línea de TurnosIA"
                            onChange={e => setWebhookOutgoing(e.target.value)}
                          >
                            <MenuItem value=""><em>Conexión predeterminada de la empresa</em></MenuItem>
                            {(webhookCfg?.lines || []).map(l => {
                              const busy = l.ocupadaPor === "webhook propio de la conexión" || l.ocupadaPor === "bot de la empresa";
                              const isTurnos = l.esLineaDeTurnosIA;
                              const tag = isTurnos
                                ? "línea actual de TurnosIA"
                                : busy
                                ? `ocupada: ${l.ocupadaPor}`
                                : "libre";
                              return (
                                <MenuItem
                                  key={l.whatsappId}
                                  value={String(l.whatsappId)}
                                  disabled={l.status !== "CONNECTED" || (busy && !isTurnos)}
                                >
                                  {l.name || `Línea ${l.whatsappId}`} — {l.status}
                                  {l.tipo === "meta" ? " · META" : ""} · {tag}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                        {(() => {
                          const selLine = (webhookCfg?.lines || []).find(l => String(l.whatsappId) === String(webhookOutgoing));
                          if (selLine && selLine.tipo === "meta") {
                            return (
                              <Typography variant="caption" style={{ display: "block", color: "#c62828", marginBottom: 8 }}>
                                ⚠ Línea Meta: el inbound/conversación funciona, pero los recordatorios proactivos NO salen hasta que exista el path de plantillas Meta.
                              </Typography>
                            );
                          }
                          return null;
                        })()}
                        <Button
                          variant="contained" color="primary" size="small" disableElevation
                          disabled={savingWebhook || (webhookCfg && !webhookCfg.provisioned)}
                          onClick={handleSaveWebhook}
                        >
                          {savingWebhook ? <CircularProgress size={16} /> : "Guardar webhook"}
                        </Button>
                      </>
                    )}
                  </Paper>
                </>
              )}
              {/* Localización (editable) — timezone + locale */}
              {turnosConfig && (
                <>
                  <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8, marginTop: 12 }}>
                    Localización
                  </Typography>
                  <Paper variant="outlined" style={{ padding: "14px 16px", marginBottom: 16, borderRadius: 8 }}>
                    <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
                      Zona horaria (IANA) y locale de la empresa. Los recordatorios y horarios usan estos valores. Se guardan con "Guardar cambios".
                    </Typography>
                    <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                      <TextField
                        label="Zona horaria (IANA)"
                        variant="outlined"
                        size="small"
                        style={{ minWidth: 260 }}
                        value={featureForm.timezone || ""}
                        onChange={e => { const v = e.target.value; setFeatureForm(prev => ({ ...prev, timezone: v })); }}
                        placeholder="America/Argentina/Cordoba"
                      />
                      <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
                        <InputLabel>Locale</InputLabel>
                        <Select
                          value={featureForm.locale || "es-AR"}
                          label="Locale"
                          onChange={e => { const v = e.target.value; setFeatureForm(prev => ({ ...prev, locale: v })); }}
                        >
                          {["es-AR", "it-IT", "en-US", "pt-BR"].map(l => (
                            <MenuItem key={l} value={l}>{l}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </Paper>
                </>
              )}
              {/* Plan comercial — gatea los modos de operación del admin */}
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8, marginTop: 12 }}>
                Plan
              </Typography>
              <Paper variant="outlined" style={{ padding: "14px 16px", marginBottom: 16, borderRadius: 8 }}>
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
                  El plan define qué <strong>modos de operación</strong> puede elegir el admin de la empresa. Los modos no incluidos aparecen bloqueados ("contactanos"). El backend valida el gate.
                </Typography>
                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                    <InputLabel>Plan</InputLabel>
                    <Select value={planValue} label="Plan" onChange={e => setPlanValue(e.target.value)}>
                      {PLAN_OPTIONS.map(o => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained" color="primary" size="small"
                    disableElevation
                    disabled={savingPlan || planValue === savedPlan}
                    onClick={handleSavePlan}
                  >
                    {savingPlan ? <CircularProgress size={16} /> : "Guardar plan"}
                  </Button>
                </div>
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 10 }}>
                  {(PLAN_OPTIONS.find(o => o.value === planValue) || {}).enables}
                </Typography>
              </Paper>

              {/* Plantilla por rubro (TurnosIA universal) */}
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8, marginTop: 12 }}>
                Plantilla de rubro
              </Typography>
              {(() => {
                // Idioma efectivo de la empresa (el que se está por guardar, o el guardado).
                const companyLocale = featureForm.locale || turnosConfig?.locale || "es-AR";
                // Solo se ofrecen plantillas ACTIVAS del idioma de la empresa + las neutrales.
                const offered = (rubroTemplates || []).filter(
                  (t) => t.active && (t.locale === companyLocale || t.locale == null)
                );
                const current = (rubroTemplates || []).find((t) => t.key === turnosConfig?.rubroTemplate);
                const currentLabel = current?.label || turnosConfig?.rubroTemplate || "ninguno";
                return (
                  <Paper variant="outlined" style={{ padding: "14px 16px", marginBottom: 16, borderRadius: 8 }}>
                    <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
                      Rubro actual: <strong>{currentLabel}</strong>. Solo se ofrecen plantillas del idioma
                      de la empresa (<strong>{companyLocale}</strong>) y las neutrales. Aplicar suma
                      especialidades, campos y términos del rubro (idempotente, no pisa lo editado).
                    </Typography>
                    {offered.length === 0 ? (
                      <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                        No hay plantillas para {companyLocale}. Creá una desde la pestaña{" "}
                        <strong>Plantillas de rubro</strong>.
                      </Typography>
                    ) : (
                      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                        <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
                          <InputLabel>Rubro</InputLabel>
                          {/* Guarda: el value del Select SIEMPRE debe existir entre las
                              opciones ofrecidas. El rubro guardado se preselecciona, pero
                              si aún no está en `offered` (catálogo cargando, o locale que
                              no lo incluye) caemos a "" en vez de pasar un value fuera de
                              rango (MUI warnea en dev y ensucia la consola). */}
                          <Select
                            value={offered.some((t) => t.key === rubroToApply) ? rubroToApply : ""}
                            label="Rubro"
                            onChange={e => setRubroToApply(e.target.value)}
                          >
                            <MenuItem value=""><em>Seleccionar...</em></MenuItem>
                            {offered.map((t) => (
                              <MenuItem key={t.key} value={t.key}>
                                {t.label}{t.locale == null ? " (neutral)" : ""}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          variant="outlined" color="primary" size="small"
                          disabled={!rubroToApply || applyingRubro}
                          onClick={handleApplyRubro}
                        >
                          {applyingRubro ? <CircularProgress size={16} /> : "Aplicar plantilla"}
                        </Button>
                      </div>
                    )}
                  </Paper>
                );
              })()}

              {/* FASE 3 — Guía de pasos del flujo de plantillas (para el reseller) */}
              <Paper variant="outlined" style={{ padding: "14px 16px", marginBottom: 16, marginTop: 12, borderRadius: 8, background: "#f4f9ff", borderColor: "#b6e0fe" }}>
                <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8, color: "#0b5394" }}>
                  Cómo configurar las plantillas de esta empresa (paso a paso)
                </Typography>
                <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.7 }}>
                  <li><strong>Elegí rubro e idioma</strong> de la empresa (arriba). Eso define el vocabulario y el idioma de las plantillas.</li>
                  <li><strong>Revisá y editá</strong> el texto de cada plantilla faltante antes de crearla (botón <em>“Revisar y editar antes de crear”</em> → se abre el editor). Podés cambiar la <strong>redacción</strong>; las <strong>variables</strong> (<code>{"{{1}}"}</code>, <code>{"{{2}}"}</code>…) quedan fijas.</li>
                  <li><strong>Crear</strong>: se envían a Meta y quedan en <Chip size="small" label="PENDING" style={{ height: 18, backgroundColor: "#ed6c02", color: "#fff" }} /> hasta que Meta las apruebe. <strong>Una vez enviada a revisión, la plantilla no se edita</strong>: para cambiarla se crea una nueva.</li>
                  <li><strong>Esperá la aprobación</strong> de Meta (suele tardar minutos a horas). Cuando estén <Chip size="small" label="APPROVED" style={{ height: 18, backgroundColor: "#2e7d32", color: "#fff" }} /> ya respaldan los envíos fuera de la ventana de 24&nbsp;h.</li>
                  <li><strong>Mapeá cada mensaje</strong> a su plantilla si hace falta (sección <em>“Mapeo manual de plantillas”</em>, abajo). Sólo se pueden elegir plantillas <strong>compatibles en variables</strong>. Sin mapear = se usa la plantilla por convención.</li>
                </ol>
              </Paper>

              {/* feat_263 — Set ESTÁNDAR de plantillas Meta de TurnosIA (vista + seed) */}
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8, marginTop: 12 }}>
                Plantillas Meta de TurnosIA (set estándar)
              </Typography>
              <Paper variant="outlined" style={{ padding: "14px 16px", marginBottom: 16, borderRadius: 8 }}>
                {stdLoading ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <CircularProgress size={16} /> <Typography variant="caption">Cargando estado…</Typography>
                  </div>
                ) : !stdTemplates ? (
                  <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                    Sin datos de plantillas.
                  </Typography>
                ) : !stdTemplates.applicable ? (
                  <Typography variant="body2" color="textSecondary">
                    {stdTemplates.reason === "qr_line"
                      ? "La línea de TurnosIA es QR (texto libre): no requiere plantillas Meta."
                      : "Esta empresa no tiene una línea Meta de TurnosIA configurada (definí la Línea de salida Meta)."}
                  </Typography>
                ) : (
                  <>
                    <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 10 }}>
                      Línea {stdTemplates.whatsappId} · idioma <strong>{stdTemplates.language}</strong>. El negocio y el
                      asistente ya vienen horneados en el texto. Al tocar <strong>“Revisar y editar antes de crear”</strong> se
                      abre un editor donde podés <strong>adaptar la redacción de cada plantilla</strong> antes de enviarla a Meta
                      (es idempotente: no re-envía las existentes). Los recuadros grises de abajo son solo el <em>texto interno
                      de referencia</em> (in-window) — <strong>no</strong> es la plantilla Meta que se edita.
                    </Typography>
                    <div>
                      {(stdTemplates.items || []).map((it) => {
                        const st = it.status;
                        const color =
                          st === "APPROVED" ? "#2e7d32"
                          : st === "PENDING" ? "#ed6c02"
                          : st === "REJECTED" ? "#d32f2f"
                          : st === "missing" ? "#9e9e9e"
                          : "#616161";
                        const label = st === "missing" ? "FALTA" : st;
                        // Correlación read-only con el texto interno equivalente (in-window).
                        const corr = TURNOS_CORRELATION_BY_META[it.name];
                        const internalLabel = corr ? TURNOS_INTERNAL_LABELS[corr.internalKey] : null;
                        const internalPreview = corr ? TURNOS_DEFAULT_INTERNAL_TEXT[corr.internalKey] : null;
                        return (
                          <div key={it.name} style={{ padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ minWidth: 190, fontFamily: "monospace", fontSize: 13 }}>{it.name}</span>
                              <Chip
                                size="small"
                                label={label}
                                style={{ backgroundColor: color, color: "#fff", fontWeight: 600, height: 20 }}
                              />
                              {it.categoryMismatch && (
                                <Chip size="small" variant="outlined" label={`cat: ${it.metaCategory}`} style={{ height: 20, borderColor: "#d32f2f", color: "#d32f2f" }} />
                              )}
                              {it.varCountMismatch && it.status !== "missing" && (
                                <Chip size="small" variant="outlined" label={`vars ${it.variableCount}/${it.expectedVarCount}`} style={{ height: 20, borderColor: "#ed6c02", color: "#ed6c02" }} />
                              )}
                              {corr?.oneToMany && (
                                <Chip size="small" variant="outlined" label="1 interno → varias Meta" style={{ height: 20, borderColor: "#7b1fa2", color: "#7b1fa2" }} />
                              )}
                              {corr?.unwired && (
                                <Chip size="small" variant="outlined" label="sin envío automático" style={{ height: 20, borderColor: "#616161", color: "#616161" }} />
                              )}
                            </div>
                            {corr && (
                              <div style={{ marginLeft: 8, marginTop: 3 }}>
                                <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                                  Evento: <strong>{corr.event}</strong> · Texto interno (in-window):{" "}
                                  <strong>{internalLabel}</strong>
                                  {corr.oneToMany && " (el mismo texto respalda recordatorio_turno y recordatorio_hoy)"}
                                  {corr.unwired && " · hoy las ofertas salen como texto libre por el bot"}
                                </Typography>
                                {internalPreview && (
                                  <pre style={{
                                    margin: "3px 0 0", padding: "6px 8px", background: "#fafafa",
                                    border: "1px solid #eee", borderRadius: 4, fontSize: 11,
                                    whiteSpace: "pre-wrap", color: "#555", fontFamily: "inherit"
                                  }}>{internalPreview}</pre>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {/* Textos internos que NUNCA usan plantilla Meta (in-window / texto libre) */}
                    <div style={{ marginTop: 12 }}>
                      <Typography variant="caption" color="textSecondary" style={{ display: "block", fontWeight: 700, marginBottom: 4 }}>
                        Sin plantilla Meta (salen siempre como texto dentro de la conversación):
                      </Typography>
                      {TURNOS_INTERNAL_WITHOUT_META.map((k) => (
                        <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 0" }}>
                          <span style={{ minWidth: 190, fontSize: 12 }}>{TURNOS_INTERNAL_LABELS[k]}</span>
                          <Chip size="small" variant="outlined" label="sin Meta" style={{ height: 20, borderColor: "#9e9e9e", color: "#757575" }} />
                          <Typography variant="caption" color="textSecondary">{TURNOS_CORRELATION_BY_INTERNAL[k]?.event}</Typography>
                        </div>
                      ))}
                    </div>
                    <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 10, fontStyle: "italic" }}>
                      {TURNOS_WINDOW_HELP}
                    </Typography>
                    <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 8, background: "#eef7ff", border: "1px solid #b6e0fe" }}>
                      <Typography variant="body2" style={{ fontWeight: 700, color: "#0b5394", marginBottom: 4 }}>
                        ✏️ Este es el momento de adaptar el texto al negocio
                      </Typography>
                      <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 10 }}>
                        Al tocar el botón se abre un <strong>editor</strong>: vas a ver el cuerpo real de cada plantilla Meta
                        (con <code>{"{{1}}"}</code>, <code>{"{{2}}"}</code>…) y podés cambiar su <strong>redacción</strong> para el
                        rubro del cliente (odontología, taller, estética…). Las <strong>variables</strong> quedan bloqueadas y
                        <strong> nada se envía a Meta</strong> hasta que confirmes dentro de ese editor.
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        disabled={seeding}
                        onClick={handleSeedTemplates}
                      >
                        {seeding ? <CircularProgress size={18} /> : "Revisar y editar antes de crear"}
                      </Button>
                    </div>
                  </>
                )}
              </Paper>

              {/* FASE 2 — Mapeo manual mensaje→plantilla Meta (override, superadmin) */}
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8, marginTop: 4 }}>
                Mapeo manual de plantillas (qué plantilla usa cada mensaje)
              </Typography>
              <Paper variant="outlined" style={{ padding: "14px 16px", marginBottom: 16, borderRadius: 8 }}>
                <TurnosTemplateOverrides
                  kinds={ovData?.kinds || []}
                  approved={ovApproved}
                  saving={ovSaving}
                  onSave={handleOvSave}
                />
              </Paper>

              <Typography variant="caption" color="textSecondary">
                Para editar horarios, profesionales y plantillas: acceder como admin de la empresa desde <strong>/turnos → Ajustes</strong>.
              </Typography>

              <div style={{ height: 20 }} />
              <MetricsResetSection company={selectedCompany} />
            </>
          )}

          {/* Settings WAIA de la empresa */}
          {!selectedCompany?.turnosEnabled && (
            <>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                Esta empresa no tiene TurnosIA habilitado en WAIA (setting <code>turnosIAEnabled</code>).
              </Typography>
              {selectedSettings.length > 0 && (
                <Paper variant="outlined" style={{ padding: "8px 16px", borderRadius: 8 }}>
                  {selectedSettings.filter(s => s.key).sort((a, b) => a.key.localeCompare(b.key)).map(s => (
                    <div key={s.key} className={classes.settingRow}>
                      <span className={classes.settingKey}>{s.key}</span>
                      <span className={classes.settingVal}>{s.value || "—"}</span>
                    </div>
                  ))}
                </Paper>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <Button onClick={() => setDialogOpen(false)}>Cerrar</Button>
          {selectedCompany?.turnosEnabled && (
            <Button onClick={handleSaveFeatures} color="primary" variant="contained" disabled={saving || settingsLoading}>
              {saving ? <CircularProgress size={18} /> : "Guardar cambios"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* FASE 1 — Editor de prosa ANTES de crear (variables bloqueadas). */}
      <Dialog open={!!seedDialog} onClose={() => !seedSubmitting && setSeedDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Revisá y editá el texto antes de crear</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" style={{ marginBottom: 12 }}>
            Podés cambiar la <strong>redacción</strong> de cada plantilla. Las <strong>variables</strong>{" "}
            (<code>{"{{1}}"}</code>, <code>{"{{2}}"}</code>…) son fichas fijas: no se pueden borrar,
            agregar ni reordenar. El nombre, la categoría y el idioma quedan fijos.
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 16, color: "#b26a00", background: "#fff8e1", border: "1px solid #ffe0a3", borderRadius: 6, padding: "8px 12px" }}>
            ⚠ Una vez enviada a revisión, la plantilla <strong>no se edita</strong>: para cambiarla, se crea una nueva.
          </Typography>
          {(seedDialog?.items || []).map((it) => {
            const edited = seedEdits[it.name] != null ? seedEdits[it.name] : it.bodyText;
            const valid = editedBodyValid(edited, it.varOrder.length);
            return (
              <div key={it.name} style={{ marginBottom: 18, paddingBottom: 12, borderBottom: "1px solid #eee" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{it.name}</span>
                  <Chip size="small" label={it.category} style={{ height: 20 }} />
                  <Chip size="small" variant="outlined" label={it.language} style={{ height: 20 }} />
                </div>
                <div style={{ marginBottom: 6, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                  <Typography variant="caption" color="textSecondary">Variables fijas:</Typography>
                  {it.varOrder.map((v, idx) => (
                    <Chip key={v} size="small" label={`{{${idx + 1}}} = ${v}`} style={{ height: 20, fontFamily: "monospace", fontSize: 11 }} />
                  ))}
                </div>
                <TextField
                  fullWidth multiline minRows={3} variant="outlined" size="small"
                  value={edited}
                  error={!valid}
                  helperText={valid
                    ? " "
                    : `Deben quedar exactamente ${it.varOrder.length} variables correlativas {{1}}..{{${it.varOrder.length}}}.`}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSeedEdits((prev) => ({ ...prev, [it.name]: v }));
                  }}
                />
              </div>
            );
          })}
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <Button onClick={() => setSeedDialog(null)} disabled={seedSubmitting}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={seedSubmitting}
            onClick={handleSeedConfirm}
          >
            {seedSubmitting ? <CircularProgress size={18} /> : `Crear ${seedDialog?.items?.length || 0} plantilla(s) en Meta`}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Tab 2 — API Docs
// ─────────────────────────────────────────────────────────────────────────────
const ApiDocsTab = () => {
  const classes = useStyles();
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api
      .get("/superadmin/turnos/api-docs")
      .then(({ data }) => {
        setDocs(data);
        setError(null);
      })
      .catch(() => {
        setError("No se pudo obtener la documentación del backend de TurnosIA.");
      })
      .finally(() => setLoading(false));
  }, []);

  const handleCopy = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => toast.success("Copiado al portapapeles"));
    }
  };

  if (loading) {
    return <CircularProgress size={32} style={{ display: "block", margin: "40px auto" }} />;
  }

  if (error || !docs) {
    return (
      <Paper className={classes.section}>
        <Typography color="error" gutterBottom>
          {error || "Error desconocido"}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Verificar manualmente:
        </Typography>
        <div className={classes.codeBlock}>
          {`# Vía backend WAIA (con tu sesión de superadmin):\ncurl -s "https://apiwaia.zzia.info/superadmin/turnos/api-docs" \\\n  -H "Authorization: Bearer <TU_JWT_WAIA>"`}
        </div>
      </Paper>
    );
  }

  const allEndpoints = docs.endpoints || [];
  const filtered = search.trim()
    ? allEndpoints.filter(
        (ep) =>
          ep.endpoint.toLowerCase().includes(search.toLowerCase()) ||
          (ep.description || "").toLowerCase().includes(search.toLowerCase())
      )
    : allEndpoints;

  // Agrupar por categoría
  const grouped = {};
  filtered.forEach((ep) => {
    const cat = getCategory(ep.endpoint);
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(ep);
  });

  const orderedCategories = [
    ...CATEGORY_ORDER.filter((c) => grouped[c]),
    ...Object.keys(grouped).filter((c) => !CATEGORY_ORDER.includes(c)),
  ];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <TextField
          label="Buscar endpoint o descripción"
          size="small"
          variant="outlined"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: "1 1 320px", maxWidth: 420 }}
          placeholder="ej: appointment, cancel, professional..."
        />
        <Typography variant="body2" color="textSecondary">
          {filtered.length} endpoint{filtered.length !== 1 ? "s" : ""} documentado
          {filtered.length !== 1 ? "s" : ""}
          {search && allEndpoints.length !== filtered.length
            ? ` (de ${allEndpoints.length})`
            : ""}
        </Typography>
      </div>

      <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
        Base URL: <code>{turnosApiUrl}</code> — Autenticación: JWT WAIA (salvo endpoints de superadmin y health)
      </Typography>
      <Divider style={{ marginBottom: 12 }} />

      {orderedCategories.length === 0 && (
        <Typography color="textSecondary" style={{ padding: 24, textAlign: "center" }}>
          Sin resultados para "{search}"
        </Typography>
      )}

      {orderedCategories.map((cat) => (
        <div key={cat}>
          <Typography className={classes.categoryHeader}>{cat}</Typography>
          {grouped[cat].map((ep, i) => {
            const method = getMethod(ep.endpoint);
            const path = getPath(ep.endpoint);
            const methodColor = METHOD_COLORS[method] || "#666";

            return (
              <Accordion
                key={i}
                elevation={0}
                className={classes.accordion}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ alignItems: "center", padding: "0 16px" }}>
                  <span className={classes.method} style={{ backgroundColor: methodColor }}>
                    {method}
                  </span>
                  <Typography style={{ fontFamily: "monospace", fontSize: 13, flexGrow: 1 }}>
                    {path}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ marginLeft: 8, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {ep.description}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ flexDirection: "column", gap: 8, padding: "8px 16px 20px" }}>
                  <Typography variant="body2" style={{ marginBottom: 8 }}>
                    {ep.description}
                  </Typography>

                  {ep.auth && (
                    <Typography variant="caption" color="textSecondary">
                      Auth: <code>{ep.auth}</code>
                    </Typography>
                  )}

                  {ep.queryParams && Object.keys(ep.queryParams).length > 0 && (
                    <>
                      <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
                        Query params:
                      </Typography>
                      <div className={classes.codeBlock}>
                        {JSON.stringify(ep.queryParams, null, 2)}
                      </div>
                    </>
                  )}

                  {ep.body && Object.keys(ep.body).length > 0 && (
                    <>
                      <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
                        Body (JSON):
                      </Typography>
                      <div className={classes.codeBlock}>
                        {JSON.stringify(ep.body, null, 2)}
                      </div>
                    </>
                  )}

                  {ep.response && (
                    <>
                      <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
                        Respuesta:
                      </Typography>
                      <div className={classes.codeBlock}>
                        {typeof ep.response === "string"
                          ? ep.response
                          : JSON.stringify(ep.response, null, 2)}
                      </div>
                    </>
                  )}

                  {ep.curl && (
                    <>
                      <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
                        Ejemplo curl:
                      </Typography>
                      <div className={classes.curlWrapper}>
                        <div className={classes.codeBlock}>{ep.curl}</div>
                        <Tooltip title="Copiar curl">
                          <IconButton
                            size="small"
                            className={classes.copyBtn}
                            onClick={() => handleCopy(ep.curl)}
                          >
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Tab 3 — Recordatorios (default global de la ventana de envío)
// ─────────────────────────────────────────────────────────────────────────────
const ReminderWindowGlobalTab = () => {
  const classes = useStyles();
  const [data, setData] = useState(null); // { effective, stored, hardcoded }
  const [form, setForm] = useState(rwToForm(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const apply = (d) => {
    setData(d || null);
    setForm(rwToForm(d?.effective));
  };

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/superadmin/turnos/reminder-window")
      .then(({ data: d }) => apply(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleDay = (code) =>
    setForm((prev) => ({
      ...prev,
      diasEvitar: prev.diasEvitar.includes(code)
        ? prev.diasEvitar.filter((d) => d !== code)
        : [...prev.diasEvitar, code],
    }));

  const toggleSabadoPropio = (checked) =>
    setForm((prev) => ({
      ...prev,
      sabadoHasta: checked ? prev.sabadoHasta || prev.horaHasta || "13:00" : null,
    }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.patch("/superadmin/turnos/reminder-window", {
        horaDesde: form.horaDesde,
        horaHasta: form.horaHasta,
        diasEvitar: form.diasEvitar,
        sabadoHasta: form.sabadoHasta,
      });
      apply(d);
      toast.success("Default global guardado");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error al guardar");
    }
    setSaving(false);
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.patch("/superadmin/turnos/reminder-window", { reset: true });
      apply(d);
      toast.success("Restablecido al default del sistema");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error al restablecer");
    }
    setSaving(false);
  };

  if (loading) {
    return <CircularProgress size={32} style={{ display: "block", margin: "40px auto" }} />;
  }

  const usingHardcoded = !data?.stored;

  return (
    <div>
      <Typography variant="h6" className={classes.sectionTitle}>
        Ventana de envío de recordatorios
      </Typography>
      <Typography variant="body2" className={classes.sectionDesc}>
        Este es el default que usan todas las empresas que no tengan su propia configuración.
      </Typography>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <Chip
          size="small"
          className={classes.statusChip}
          label={usingHardcoded ? "Usando default del sistema" : "Default global personalizado"}
          style={{
            backgroundColor: usingHardcoded ? "#f5f5f5" : "#e8f5e9",
            color: usingHardcoded ? "#616161" : "#2e7d32",
          }}
        />
        <Typography variant="caption" color="textSecondary">
          Default del sistema: {data?.hardcoded?.horaDesde}–{data?.hardcoded?.horaHasta}, evita{" "}
          {(data?.hardcoded?.diasEvitar || []).join(", ") || "—"}, sáb{" "}
          {data?.hardcoded?.sabadoHasta || data?.hardcoded?.horaHasta}
        </Typography>
      </div>

      <Paper className={classes.section} style={{ maxWidth: 600 }}>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
          <TextField
            label="Hora desde"
            type="time"
            variant="outlined"
            size="small"
            style={{ flex: "1 1 180px" }}
            value={form.horaDesde}
            onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, horaDesde: v })); }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Hora hasta"
            type="time"
            variant="outlined"
            size="small"
            style={{ flex: "1 1 180px" }}
            value={form.horaHasta}
            onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, horaHasta: v })); }}
            InputLabelProps={{ shrink: true }}
          />
        </div>

        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 6 }}>
          Días a evitar
        </Typography>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
          {RW_DAYS.map((d) => {
            const active = form.diasEvitar.includes(d.code);
            return (
              <Chip
                key={d.code}
                label={d.label}
                size="small"
                onClick={() => toggleDay(d.code)}
                color={active ? "secondary" : "default"}
                variant={active ? "default" : "outlined"}
                style={{ cursor: "pointer" }}
              />
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={form.sabadoHasta !== null}
                onChange={(e) => toggleSabadoPropio(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Sábado con horario propio</Typography>}
          />
          {form.sabadoHasta !== null && (
            <TextField
              label="Sábado hasta"
              type="time"
              variant="outlined"
              size="small"
              style={{ width: 160 }}
              value={form.sabadoHasta}
              onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, sabadoHasta: v })); }}
              InputLabelProps={{ shrink: true }}
            />
          )}
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : "Guardar default global"}
          </Button>
          <Button variant="outlined" onClick={handleReset} disabled={saving || usingHardcoded}>
            Restablecer al default del sistema
          </Button>
        </div>

        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 16 }}>
          Los recordatorios que caerían fuera de esta franja o en un día evitado se corren al momento
          válido más cercano antes del turno.
        </Typography>
      </Paper>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Tab 4 — Plantillas de rubro (terminología universal por rubro)
// ─────────────────────────────────────────────────────────────────────────────
const emptyTerminology = () =>
  TERMINOLOGY_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

const RubroTemplatesTab = () => {
  const classes = useStyles();
  const [templates, setTemplates] = useState([]);
  const [localeDefaults, setLocaleDefaults] = useState(LOCALE_DEFAULTS_ES_FALLBACK);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isCreate, setIsCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingKey, setTogglingKey] = useState(null);
  const [form, setForm] = useState({ key: "", label: "", locale: "", terminology: emptyTerminology() });

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/turnos/rubro-templates");
      setTemplates(Array.isArray(data?.templates) ? data.templates : []);
      // El backend devuelve los defaults del locale por clave (lo que hereda un campo
      // vacío). Si viene, lo usamos para el placeholder "hereda: X"; si no, el fallback.
      if (data?.localeDefaults && typeof data.localeDefaults === "object") {
        setLocaleDefaults({ ...LOCALE_DEFAULTS_ES_FALLBACK, ...data.localeDefaults });
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || "Error al cargar plantillas de rubro");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const openEdit = (t) => {
    setIsCreate(false);
    setForm({
      key: t.key,
      label: t.label || "",
      locale: t.locale || "",   // "" = neutral
      terminology: { ...emptyTerminology(), ...(t.terminology || {}) },
    });
    setDialogOpen(true);
  };

  const openCreate = () => {
    setIsCreate(true);
    setForm({ key: "", label: "", locale: "", terminology: emptyTerminology() });
    setDialogOpen(true);
  };

  const setTermValue = (key, value) =>
    setForm((prev) => ({ ...prev, terminology: { ...prev.terminology, [key]: value } }));

  // Solo se envían los términos no vacíos (mapa de reemplazo completo del PATCH).
  const buildTerminologyPayload = () => {
    const out = {};
    Object.entries(form.terminology).forEach(([k, v]) => {
      if (v && String(v).trim()) out[k] = String(v).trim();
    });
    return out;
  };

  const handleSave = async () => {
    const label = form.label.trim();
    if (!label) {
      toast.error("El nombre (label) es obligatorio");
      return;
    }
    const terminology = buildTerminologyPayload();
    setSaving(true);
    try {
      if (isCreate) {
        const key = form.key.trim().toLowerCase();
        if (!key || !/^[a-z0-9_]+$/.test(key)) {
          toast.error("La key es obligatoria (solo minúsculas, dígitos y guión bajo)");
          setSaving(false);
          return;
        }
        // Solo se manda label + locale + terminology (specialties/customFields fuera de alcance).
        await api.post("/superadmin/turnos/rubro-templates", { key, label, locale: form.locale, terminology });
        toast.success("Plantilla creada");
      } else {
        // PATCH solo reemplaza lo enviado → omitir specialties/customFields los preserva.
        await api.patch(`/superadmin/turnos/rubro-templates/${form.key}`, { label, locale: form.locale, terminology });
        toast.success("Plantilla actualizada");
      }
      setDialogOpen(false);
      await loadTemplates();
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error("Ya existe un rubro con esa key");
      } else {
        toast.error(e?.response?.data?.error || "Error al guardar la plantilla");
      }
    }
    setSaving(false);
  };

  // Alterna activa/inactiva reflejando el estado real. Activar = PATCH {active:true};
  // desactivar = PATCH {active:false} (mismo efecto que el DELETE soft, pero simétrico
  // y sin tocar terminology/label). Confirmación solo al desactivar.
  const handleToggleActive = async (t) => {
    const activate = !t.active;
    if (!activate) {
      if (!window.confirm(
        `¿Desactivar la plantilla "${t.label || t.key}"?\n\n` +
        `No se podrá asignar a empresas nuevas. Las empresas que ya la tienen ` +
        `seguirán operando, pero dejarán de heredar el vocabulario propio del rubro ` +
        `(pasan a usar la palabra del idioma) hasta reactivarla.`
      )) return;
    }
    setTogglingKey(t.key);
    try {
      await api.patch(`/superadmin/turnos/rubro-templates/${t.key}`, { active: activate });
      toast.success(activate ? "Plantilla activada" : "Plantilla desactivada");
      await loadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Error al cambiar el estado de la plantilla");
    } finally {
      setTogglingKey(null);
    }
  };

  if (loading) {
    return <CircularProgress size={32} style={{ display: "block", margin: "40px auto" }} />;
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
        <Typography variant="body2" color="textSecondary" style={{ maxWidth: 720, lineHeight: 1.6 }}>
          Las plantillas definen el <strong>vocabulario base</strong> de cada tipo de negocio
          (consultorio, taller, estética…). Cuando le asignás un rubro a una empresa, esa empresa
          <strong> hereda estas palabras</strong>. Después cada empresa puede ajustar las suyas sin
          perder la plantilla.
        </Typography>
        <Button variant="contained" color="primary" disableElevation onClick={openCreate} style={{ flexShrink: 0 }}>
          Crear plantilla
        </Button>
      </div>

      {/* Ayuda del estado activa/inactiva (comportamiento real confirmado en backend). */}
      <div className={classes.infoBox} style={{ marginTop: 0, marginBottom: 20, display: "flex", gap: 10, alignItems: "flex-start" }}>
        <InfoOutlinedIcon style={{ fontSize: 18, color: "#f59e0b", marginTop: 1, flexShrink: 0 }} />
        <Typography variant="caption" color="textSecondary" style={{ lineHeight: 1.6 }}>
          <strong>Activa vs inactiva:</strong> una plantilla inactiva no se ofrece al asignar el rubro
          a una empresa nueva. Las empresas que ya la tienen asignada siguen funcionando, pero dejan de
          heredar el vocabulario propio del rubro (pasan a usar la palabra del idioma) hasta reactivarla.
          Las plantillas de <strong>Sistema</strong> (base de cada vertical) no se pueden desactivar
          porque hay empresas que dependen de ellas.
        </Typography>
      </div>

      {templates.length === 0 && (
        <Paper className={classes.section} style={{ padding: 40, textAlign: "center" }}>
          <Typography color="textSecondary">No hay plantillas de rubro cargadas.</Typography>
        </Paper>
      )}

      {templates.length > 0 && (
        <Paper className={classes.tableCard}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Key</TableCell>
                <TableCell>Idioma</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((t) => (
                <TableRow key={t.key}>
                  <TableCell><strong>{t.label || t.key}</strong></TableCell>
                  <TableCell style={{ fontFamily: "monospace", fontSize: 13 }}>{t.key}</TableCell>
                  <TableCell>
                    <Chip
                      label={t.locale ? localeChipLabel(t.locale) : "Neutral"}
                      size="small"
                      className={classes.statusChip}
                      style={t.locale
                        ? { backgroundColor: "#e3f2fd", color: "#1565c0" }
                        : { backgroundColor: "#f5f5f5", color: "#616161" }}
                    />
                  </TableCell>
                  <TableCell>
                    {t.isSystem && (
                      <Chip
                        label="Sistema"
                        size="small"
                        className={classes.statusChip}
                        style={{ backgroundColor: "#ede7f6", color: "#5e35b1" }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t.active ? "Activa" : "Inactiva"}
                      size="small"
                      className={classes.statusChip}
                      style={{
                        backgroundColor: t.active ? "#e8f5e9" : "#fce4ec",
                        color: t.active ? "#2e7d32" : "#c62828",
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" onClick={() => openEdit(t)} style={{ marginRight: 8 }}>
                      Editar
                    </Button>
                    {/* Toggle que refleja el estado real: activa → "Desactivar"; inactiva → "Activar".
                        Los rubros de sistema no se pueden desactivar → sin botón. */}
                    {!t.isSystem && (
                      togglingKey === t.key ? (
                        <CircularProgress size={16} style={{ verticalAlign: "middle", margin: "0 12px" }} />
                      ) : t.active ? (
                        <Button size="small" variant="text" style={{ color: "#c62828" }} onClick={() => handleToggleActive(t)}>
                          Desactivar
                        </Button>
                      ) : (
                        <Button size="small" variant="text" style={{ color: "#2e7d32" }} onClick={() => handleToggleActive(t)}>
                          Activar
                        </Button>
                      )
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Dialog crear/editar plantilla */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {isCreate ? "Crear plantilla de rubro" : `Editar plantilla — ${form.label || form.key}`}
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
            {isCreate && (
              <TextField
                label="Key"
                variant="outlined"
                size="small"
                required
                style={{ flex: "1 1 220px" }}
                value={form.key}
                onChange={(e) => { const v = e.target.value; setForm((prev) => ({ ...prev, key: v })); }}
                placeholder="ej: peluqueria"
                helperText="Solo minúsculas, dígitos y guión bajo. Única."
              />
            )}
            <TextField
              label="Nombre (label)"
              variant="outlined"
              size="small"
              required
              style={{ flex: "1 1 220px" }}
              value={form.label}
              onChange={(e) => { const v = e.target.value; setForm((prev) => ({ ...prev, label: v })); }}
              placeholder="ej: Odontología (IT)"
            />
            <FormControl variant="outlined" size="small" style={{ flex: "1 1 220px" }}>
              <InputLabel>Idioma de la plantilla</InputLabel>
              <Select
                label="Idioma de la plantilla"
                value={form.locale || ""}
                onChange={(e) => { const v = e.target.value; setForm((prev) => ({ ...prev, locale: v })); }}
              >
                {RUBRO_LOCALE_OPTIONS.map((o) => (
                  <MenuItem key={o.value || "neutral"} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {/* Ayuda del selector de idioma. */}
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 16, lineHeight: 1.6 }}>
            El <strong>idioma</strong> en el que está escrita esta plantilla. Solo se va a ofrecer a
            empresas de ese idioma. Dejalo en <strong>“Neutral”</strong> si la plantilla no carga ninguna
            palabra (solo hereda del idioma).
          </Typography>

          <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
            Terminología
          </Typography>

          {/* Ayuda del diálogo (para el reseller) — CONSERVAR el sentido, sobre todo la
              advertencia del idioma. */}
          <div className={classes.infoBox} style={{ marginTop: 0, marginBottom: 16 }}>
            <Typography variant="body2" style={{ marginBottom: 8, lineHeight: 1.6 }}>
              Cada término tiene dos formas: <strong>singular</strong> y <strong>plural</strong>.
            </Typography>
            <ul style={{ margin: "0 0 8px", paddingLeft: 20, lineHeight: 1.6 }}>
              <li>
                <Typography variant="body2" component="span" color="textSecondary">
                  Dejá un campo <strong>VACÍO</strong> para que use la palabra propia del idioma de la
                  empresa (en español “paciente”, en italiano “paziente”).
                </Typography>
              </li>
              <li>
                <Typography variant="body2" component="span" color="textSecondary">
                  Completalo <strong>SOLO</strong> cuando este rubro usa una palabra distinta — por
                  ejemplo, un taller dice “Cliente” donde un consultorio dice “Paciente”.
                </Typography>
              </li>
            </ul>
            <Typography variant="body2" style={{ lineHeight: 1.6, color: "#b26a00", fontWeight: 500 }}>
              ⚠ Importante: las palabras que completes acá se usan solo en empresas de <strong>este idioma</strong>
              {form.locale ? ` (${form.locale})` : " (Neutral → cualquiera)"}. Para otro idioma, creá una
              plantilla aparte (por ejemplo, “Odontología (IT)”).
            </Typography>
          </div>

          {/* Leyenda del indicador propio vs heredado. */}
          <div style={{ display: "flex", gap: 20, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#2e7d32", display: "inline-block" }} />
              <Typography variant="caption" color="textSecondary"><strong>Con valor</strong> = propio del rubro</Typography>
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", border: "1px solid #9e9e9e", display: "inline-block" }} />
              <Typography variant="caption" color="textSecondary"><strong>Vacío</strong> = hereda del idioma (placeholder gris)</Typography>
            </span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {TERMINOLOGY_FIELDS.map((f) => {
              const val = form.terminology[f.key] || "";
              const isSet = !!String(val).trim();
              const inherit = localeDefaults[f.key] || "";
              return (
                <TextField
                  key={f.key}
                  label={f.label}
                  variant="outlined"
                  size="small"
                  value={val}
                  onChange={(e) => { const v = e.target.value; setTermValue(f.key, v); }}
                  // La etiqueta SIEMPRE arriba (tenga o no valor) → el usuario ve el término.
                  InputLabelProps={{ shrink: true }}
                  // Placeholder = qué hereda, con el valor real del locale.
                  placeholder={inherit ? `hereda: ${inherit}` : "hereda del idioma"}
                  helperText={isSet ? "propio del rubro" : "heredado del idioma"}
                  FormHelperTextProps={{
                    style: { margin: "3px 2px 0", color: isSet ? "#2e7d32" : "#9e9e9e" },
                  }}
                  InputProps={{
                    endAdornment: isSet ? (
                      <InputAdornment position="end">
                        <Tooltip title="Limpiar (volver a heredar)">
                          <IconButton
                            size="small"
                            edge="end"
                            aria-label={`Limpiar ${f.label}`}
                            onClick={() => setTermValue(f.key, "")}
                          >
                            <ClearIcon style={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ) : null,
                  }}
                  // Borde/acento verde tenue cuando está seteado → se ve de un vistazo qué cambia.
                  style={isSet ? { backgroundColor: "rgba(46,125,50,0.06)", borderRadius: 4 } : undefined}
                />
              );
            })}
          </div>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={18} /> : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Página principal
// ─────────────────────────────────────────────────────────────────────────────
const SuperAdminTurnosIA = () => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div className={classes.root}>
      <div className={classes.headerBar}>
        <CalendarTodayIcon color="primary" />
        <Typography variant="h5" component="h1">
          TurnosIA — Panel Superadmin
        </Typography>
      </div>
      <Typography variant="body2" className={classes.pageSubtitle}>
        Gestión de empresas con TurnosIA habilitado y documentación completa de la API.
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabs}
      >
        <Tab label="Empresas" />
        <Tab label="Recordatorios (default global)" />
        <Tab label="Plantillas de rubro" />
        <Tab label="API Docs" />
      </Tabs>

      {activeTab === 0 && <EmpresasTab />}
      {activeTab === 1 && <ReminderWindowGlobalTab />}
      {activeTab === 2 && <RubroTemplatesTab />}
      {activeTab === 3 && <ApiDocsTab />}
    </div>
  );
};

export default SuperAdminTurnosIA;
